import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonMatrix = () => {
  const [activeCategory, setActiveCategory] = useState('cost');

  const categories = [
    {
      id: 'cost',
      name: 'Total Cost of Ownership',
      icon: 'DollarSign',
      description: 'Long-term financial impact and cost predictability'
    },
    {
      id: 'flexibility',
      name: 'Customization & Flexibility',
      icon: 'Settings',
      description: 'Ability to modify and adapt to specific needs'
    },
    {
      id: 'security',
      name: 'Security & Control',
      icon: 'Shield',
      description: 'Data sovereignty and security management'
    },
    {
      id: 'innovation',
      name: 'Innovation Speed',
      icon: 'Zap',
      description: 'Pace of feature development and deployment'
    }
  ];

  const comparisons = {
    cost: {
      proprietary: {
        title: "Proprietary AI Solutions",
        score: 2,
        items: [
          { label: "Initial Cost", value: "Low", icon: "Check", color: "text-green-500" },
          { label: "Ongoing Licensing", value: "High & Increasing", icon: "TrendingUp", color: "text-red-500" },
          { label: "Vendor Lock-in Risk", value: "Very High", icon: "Lock", color: "text-red-500" },
          { label: "Hidden Costs", value: "Significant", icon: "AlertTriangle", color: "text-yellow-500" },
          { label: "Cost Predictability", value: "Poor", icon: "X", color: "text-red-500" },
          { label: "Exit Costs", value: "Extremely High", icon: "X", color: "text-red-500" }
        ]
      },
      opensource: {
        title: "Open-Source AI Solutions",
        score: 9,
        items: [
          { label: "Initial Cost", value: "Moderate", icon: "Minus", color: "text-yellow-500" },
          { label: "Ongoing Licensing", value: "Zero", icon: "Check", color: "text-green-500" },
          { label: "Vendor Lock-in Risk", value: "None", icon: "Unlock", color: "text-green-500" },
          { label: "Hidden Costs", value: "Minimal", icon: "Check", color: "text-green-500" },
          { label: "Cost Predictability", value: "Excellent", icon: "Check", color: "text-green-500" },
          { label: "Exit Costs", value: "Zero", icon: "Check", color: "text-green-500" }
        ]
      }
    },
    flexibility: {
      proprietary: {
        title: "Proprietary AI Solutions",
        score: 3,
        items: [
          { label: "Code Access", value: "None", icon: "X", color: "text-red-500" },
          { label: "Custom Modifications", value: "Limited", icon: "AlertTriangle", color: "text-yellow-500" },
          { label: "Integration Options", value: "Vendor APIs Only", icon: "X", color: "text-red-500" },
          { label: "Data Format Control", value: "Vendor Controlled", icon: "X", color: "text-red-500" },
          { label: "Deployment Options", value: "Vendor Cloud Only", icon: "X", color: "text-red-500" },
          { label: "Feature Requests", value: "Vendor Roadmap", icon: "Clock", color: "text-yellow-500" }
        ]
      },
      opensource: {
        title: "Open-Source AI Solutions",
        score: 10,
        items: [
          { label: "Code Access", value: "Full Source Code", icon: "Check", color: "text-green-500" },
          { label: "Custom Modifications", value: "Unlimited", icon: "Check", color: "text-green-500" },
          { label: "Integration Options", value: "Any System", icon: "Check", color: "text-green-500" },
          { label: "Data Format Control", value: "Complete Control", icon: "Check", color: "text-green-500" },
          { label: "Deployment Options", value: "Anywhere", icon: "Check", color: "text-green-500" },
          { label: "Feature Requests", value: "Immediate Implementation", icon: "Check", color: "text-green-500" }
        ]
      }
    },
    security: {
      proprietary: {
        title: "Proprietary AI Solutions",
        score: 4,
        items: [
          { label: "Code Transparency", value: "Black Box", icon: "X", color: "text-red-500" },
          { label: "Data Location", value: "Vendor Servers", icon: "AlertTriangle", color: "text-yellow-500" },
          { label: "Security Audits", value: "Vendor Dependent", icon: "X", color: "text-red-500" },
          { label: "Compliance Control", value: "Limited", icon: "AlertTriangle", color: "text-yellow-500" },
          { label: "Vulnerability Response", value: "Vendor Timeline", icon: "Clock", color: "text-yellow-500" },
          { label: "Data Sovereignty", value: "Compromised", icon: "X", color: "text-red-500" }
        ]
      },
      opensource: {
        title: "Open-Source AI Solutions",
        score: 9,
        items: [
          { label: "Code Transparency", value: "Full Visibility", icon: "Check", color: "text-green-500" },
          { label: "Data Location", value: "Your Infrastructure", icon: "Check", color: "text-green-500" },
          { label: "Security Audits", value: "Continuous Community", icon: "Check", color: "text-green-500" },
          { label: "Compliance Control", value: "Complete", icon: "Check", color: "text-green-500" },
          { label: "Vulnerability Response", value: "Immediate", icon: "Check", color: "text-green-500" },
          { label: "Data Sovereignty", value: "Guaranteed", icon: "Check", color: "text-green-500" }
        ]
      }
    },
    innovation: {
      proprietary: {
        title: "Proprietary AI Solutions",
      score: 3,
        items: [
          { label: "Feature Development", value: "Vendor Pace", icon: "Clock", color: "text-yellow-500" },
          { label: "Custom Features", value: "Expensive/Impossible", icon: "X", color: "text-red-500" },
          { label: "Community Innovation", value: "None", icon: "X", color: "text-red-500" },
          { label: "Experimental Features", value: "Vendor Beta Only", icon: "AlertTriangle", color: "text-yellow-500" },
          { label: "Integration Speed", value: "Slow", icon: "X", color: "text-red-500" },
          { label: "Competitive Advantage", value: "Shared with All Users", icon: "X", color: "text-red-500" }
        ]
      },
      opensource: {
        title: "Open-Source AI Solutions",
        score: 10,
        items: [
          { label: "Feature Development", value: "Your Pace", icon: "Check", color: "text-green-500" },
          { label: "Custom Features", value: "Unlimited", icon: "Check", color: "text-green-500" },
          { label: "Community Innovation", value: "Global Collaboration", icon: "Check", color: "text-green-500" },
          { label: "Experimental Features", value: "Immediate Access", icon: "Check", color: "text-green-500" },
          { label: "Integration Speed", value: "Rapid", icon: "Check", color: "text-green-500" },
          { label: "Competitive Advantage", value: "Unique to You", icon: "Check", color: "text-green-500" }
        ]
      }
    }
  };

  const currentComparison = comparisons?.[activeCategory];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Enterprise AI: Side-by-Side Comparison
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Evaluate proprietary versus open-source AI across the dimensions that matter most 
            to enterprise decision-makers.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setActiveCategory(category?.id)}
              className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeCategory === category?.id
                  ? 'bg-brand-primary text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon name={category?.icon} size={20} className="mr-2" />
              <span className="hidden sm:inline">{category?.name}</span>
              <span className="sm:hidden">{category?.name?.split(' ')?.[0]}</span>
            </button>
          ))}
        </div>

        {/* Active Category Description */}
        <div className="text-center mb-12">
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {categories?.find(c => c?.id === activeCategory)?.description}
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Proprietary Column */}
          <div className="bg-white rounded-2xl shadow-executive overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {currentComparison?.proprietary?.title}
                </h3>
                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-2">Score:</span>
                  <div className="flex">
                    {[...Array(10)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={16}
                        className={i < currentComparison?.proprietary?.score ? 'text-yellow-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {currentComparison?.proprietary?.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                    <span className="font-medium text-gray-700">{item?.label}</span>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">{item?.value}</span>
                      <Icon name={item?.icon} size={16} className={item?.color} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Open Source Column */}
          <div className="bg-white rounded-2xl shadow-executive overflow-hidden">
            <div className="bg-green-50 border-b border-green-200 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {currentComparison?.opensource?.title}
                </h3>
                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-2">Score:</span>
                  <div className="flex">
                    {[...Array(10)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={16}
                        className={i < currentComparison?.opensource?.score ? 'text-yellow-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {currentComparison?.opensource?.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                    <span className="font-medium text-gray-700">{item?.label}</span>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">{item?.value}</span>
                      <Icon name={item?.icon} size={16} className={item?.color} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-12 bg-brand-primary rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">
            The Verdict is Clear
          </h3>
          <p className="text-blue-100 text-lg mb-6 max-w-3xl mx-auto">
            Across every dimension that matters to enterprise success, open-source AI delivers 
            superior value, control, and long-term strategic advantage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
              Download Full Comparison Report
            </button>
            <button className="border border-white text-white hover:bg-white hover:text-brand-primary px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
              Schedule Strategy Session
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonMatrix;